import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthModal } from '../../contexts/AuthModalContext';
import {
  House,
  Settings,
  HelpCircle,
  Menu,
  Layers,
  LogOut,
  X,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Package,
  Store,
  Heart,
  Receipt,
  Briefcase,
  MessageSquare,
  CreditCard,
  Crown,
  AlertCircle,
} from 'lucide-react';
import MobileCurrencyMenu from './MobileCurrencyMenu';
import { useMessagingStore } from '@/features/messaging/hooks/useMessagingStore';
import MessagesIcon from '@/features/messaging/components/MessagesIcon';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { clearCredentials } from '@/store/slices/authSlice';

interface SidebarLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick?: () => void;
  badgeCount?: number;
}

interface SidebarSubItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  comingSoon?: boolean;
}

interface SidebarLinkData {
  to: string;
  icon: React.ReactNode;
  label: string;
  badgeCount?: number;
  hasDropdown?: boolean;
  subItems?: SidebarSubItem[];
}

const SidebarLink: React.FC<SidebarLinkProps & { isExpanded: boolean }> = ({ to, icon, label, isActive, onClick, isExpanded, badgeCount }) => {
  const handleClick = (e: React.MouseEvent) => {
    // Only prevent default for special actions (like logout), not for regular navigation
    if (onClick && (label === 'Logout' || to === '#')) {
      e.preventDefault();
      onClick();
    } else if (onClick) {
      // For regular navigation with onClick (like mobile menu close), don't prevent default
      onClick();
    }
  };

  const isLogout = label === 'Logout';

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={`flex items-center ${isExpanded ? 'px-4' : 'px-2 justify-center'} py-3 text-sm font-medium rounded-lg transition-colors ${
        isActive 
          ? 'bg-[#e9b306]/10 text-gray-900 border-l-4 border-[#e9b306]' 
          : isLogout
          ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
          : 'text-gray-600 hover:bg-gray-100'
      }`}
      title={!isExpanded ? label : undefined}
    >
      <span className={`${isExpanded ? 'mr-3' : ''} relative`}>
        {icon}
        {!isExpanded && badgeCount !== undefined && badgeCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#E9B306] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </span>
      {isExpanded && <span>{label}</span>}
      {isExpanded && badgeCount !== undefined && badgeCount > 0 && (
        <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#E9B306] text-white text-[10px] font-bold">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </Link>
  );
};

interface SidebarProps {
  onToggle?: (isExpanded: boolean) => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onToggle, isMobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  
  // Check if we're on a profile page (mobile-only sidebar)
  const isProfilePage = location.pathname.includes('/social-profile/');
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const [isExpanded, setIsExpanded] = useState(true);
  const [openDropdowns, setOpenDropdowns] = useState<string[]>([]); // Default closed
  const { openLoginModal } = useAuthModal();
  const messagingStore = useMessagingStore();
  const unreadMessages = messagingStore.unreadTotal();

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    if (onToggle) {
      onToggle(newState);
    }
  };

  const handleLogout = () => {
    const currentPath = window.location.pathname;
    
    dispatch(clearCredentials());
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    if (onMobileClose) onMobileClose();
    
    // Store current location to return after re-login (except for protected routes)
    const protectedRoutes = ['/insights', '/account', '/create'];
    const isProtectedRoute = protectedRoutes.some(route => currentPath.startsWith(route));
    
    if (!isProtectedRoute && currentPath !== '/') {
      localStorage.setItem('redirectAfterLogin', currentPath);
    }
    
    openLoginModal();
  };
  
  // Get verification status from real user data
  const isVerified = user?.verified || user?.isVerified || false;
  
  const toggleDropdown = (label: string) => {
    setOpenDropdowns(prev => 
      prev.includes(label) ? prev.filter(item => item !== label) : [...prev, label]
    );
  };
  
  // Check account type
  const isCreator = user?.accountType === 'creator';
  const isBuyer = user?.accountType === 'buyer';

  const mainLinks = useMemo<SidebarLinkData[]>(() => {
    // Buyer-tailored nav: shopping + saved posts. No creator-subscription
    // surfaces — the "Payments" page is a creator subscription / FitCoin
    // settings hub and isn't relevant for buyers right now.
    if (isBuyer) {
      return [
        { to: '/', icon: <House size={20} />, label: 'Home' },
        { to: '/favorites', icon: <Heart size={20} />, label: 'Favorites' },
        { to: '/orders', icon: <Receipt size={20} />, label: 'Orders' },
        { to: '/messages', icon: <MessagesIcon size={22} active={location.pathname.startsWith('/messages')} />, label: 'Messages', badgeCount: unreadMessages },
        { to: '/account/settings', icon: <Settings size={20} />, label: 'Settings' },
      ];
    }

    return [
      { to: '/', icon: <House size={20} />, label: 'Home' },
      { to: '/insights', icon: <BarChart3 size={20} />, label: 'Insights' },
      {
        to: '/messages',
        icon: <MessagesIcon size={22} active={location.pathname.startsWith('/messages')} />,
        label: 'Messages',
        badgeCount: unreadMessages,
        hasDropdown: true,
        subItems: [
          { to: '/messages', label: 'Inbox', icon: <MessageSquare size={16} /> },
          { to: '/account/message-settings', label: 'Message Settings', icon: <Settings size={16} /> },
        ],
      },
      { to: '/account/portfolio', icon: <Layers size={20} />, label: 'My Archive' },
      { to: '/orders', icon: <Briefcase size={20} />, label: 'Orders' },
      // Manage Store dropdown - only for creator accounts
      ...(isCreator ? [{
        to: '/manage-collections',
        icon: <Store size={20} />,
        label: 'Manage Store',
        hasDropdown: true,
        subItems: [
          { to: '/manage-collections', label: 'Collections', icon: <Package size={16} /> },
          { to: '/store-settings', label: 'Store Settings', icon: <Settings size={16} /> },
        ]
      }] : []),
      { to: '/account/billing', icon: <CreditCard size={20} />, label: 'Payment' },
      // { to: '/account/billing/fitcoin', icon: <Coins size={20} />, label: 'FitCoin' },
      // { to: '/account/billing/promotion', icon: <Megaphone size={20} />, label: 'Promote' },
      // ...(isVerified ? [
      //   { to: '/account/tfb-studios', icon: <Link2 size={20} />, label: 'TFB Studios' }
      // ] : []),
      { to: '/account/settings', icon: <Settings size={20} />, label: 'Settings' },
    ];
  }, [isBuyer, isCreator, isVerified, user?.features?.verificationBadge, user?.isPremium, user?.subscriptionStatus, unreadMessages, location.pathname]);

  const bottomLinks: Array<{ to: string; icon: React.ReactNode; label: string; onClick?: () => void }> = useMemo(() => [
    { to: '/help', icon: <HelpCircle size={20} />, label: 'Help & Support' },
    { to: '#', icon: <LogOut size={20} />, label: 'Logout', onClick: handleLogout },
  ], [handleLogout]);

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-[90] top-20"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar - Slides from Right on Mobile */}
      <div className={`
        ${isExpanded ? 'w-72' : 'w-20'} bg-white ${isExpanded ? 'px-3' : 'px-2'} flex flex-col fixed z-[100] transition-all duration-300
        lg:h-[calc(100vh-5rem)] lg:top-20 lg:py-4
        h-screen top-0 pt-20 pb-6
        ${isProfilePage ? 'lg:hidden' : 'lg:left-0 lg:border-r lg:border-l-0 lg:translate-x-0 lg:z-40'}
        right-0 border-l border-gray-200 ${isMobileOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        {/* Toggle Button - Desktop only */}
        <button
          onClick={handleToggle}
          className="hidden lg:block absolute -right-3 top-6 p-1.5 bg-white rounded-full shadow-lg border border-gray-200 hover:bg-[#e9b306] hover:text-white transition-colors z-50"
        >
          <Menu size={16} />
        </button>

        {/* Mobile Close Button */}
        <button
          onClick={onMobileClose}
          className="lg:hidden absolute top-6 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
        >
          <X size={20} />
        </button>

        {isExpanded && (
          <div className="mb-6">
            {/* <h2 className="text-xl font-bold text-gray-800 px-4">Creators Archive</h2> */}
          </div>
        )}
        
        <nav className="flex-1 space-y-1 overflow-y-auto max-h-[calc(100vh-20rem)]">
          {mainLinks.map((link) => (
            <div key={link.to}>
              {link.hasDropdown ? (
                <>
                  {isExpanded ? (
                    <button
                      onClick={() => toggleDropdown(link.label)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        location.pathname.startsWith(link.to)
                          ? 'bg-[#e9b306]/10 text-gray-900 border-l-4 border-[#e9b306]'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="mr-3">{link.icon}</span>
                        <span>{link.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {link.badgeCount !== undefined && link.badgeCount > 0 && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-[#e9b306] text-white min-w-[18px] text-center">
                            {link.badgeCount > 99 ? '99+' : link.badgeCount}
                          </span>
                        )}
                        {openDropdowns.includes(link.label) ?
                          <ChevronDown size={16} /> :
                          <ChevronRight size={16} />
                        }
                      </div>
                    </button>
                  ) : (
                    <Link
                      to={link.to}
                      className={`relative w-full flex items-center justify-center px-2 py-3 text-sm font-medium rounded-lg transition-colors ${
                        location.pathname.startsWith(link.to)
                          ? 'bg-[#e9b306]/10 text-gray-900'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      title={link.label}
                      onClick={onMobileClose}
                    >
                      {link.icon}
                      {link.badgeCount !== undefined && link.badgeCount > 0 && (
                        <span className="absolute top-1 right-1 px-1 text-[9px] font-bold rounded-full bg-[#e9b306] text-white min-w-[16px] text-center">
                          {link.badgeCount > 9 ? '9+' : link.badgeCount}
                        </span>
                      )}
                    </Link>
                  )}
                  {isExpanded && openDropdowns.includes(link.label) && (
                    <div className="ml-4 mt-1 space-y-1">
                      {link.subItems?.map((subItem) => (
                        <Link
                          key={subItem.to}
                          to={subItem.comingSoon ? '#' : subItem.to}
                          onClick={(e) => {
                            if (subItem.comingSoon) {
                              e.preventDefault();
                            } else if (onMobileClose) {
                              onMobileClose();
                            }
                          }}
                          className={`flex items-center justify-between px-4 py-2 text-sm rounded-lg transition-colors ${
                            location.pathname === subItem.to
                              ? 'bg-[#e9b306]/5 text-gray-900 font-medium'
                              : subItem.comingSoon
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {subItem.icon}
                            <span>{subItem.label}</span>
                          </div>
                          {subItem.comingSoon && (
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Soon</span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <SidebarLink
                  {...link}
                  isActive={
                    // Special-case routes whose detail pages should keep the
                    // parent link highlighted (e.g. /messages/:threadId).
                    link.to === '/messages'
                      ? location.pathname.startsWith('/messages')
                      : location.pathname === link.to
                  }
                  isExpanded={isExpanded}
                  onClick={onMobileClose}
                />
              )}
            </div>
          ))}

        </nav>

        {/* Pinned footer — subscription card (designers only) sits directly
            above Help & Logout, all within the sidebar container. Kept OUT of
            the scrollable <nav> so the divider never clips the card. */}
        {isExpanded && isCreator && (() => {
          const status = user?.subscriptionStatus as string | null | undefined;
          const isActive = user?.isPremium || status === 'active';
          const needsRenewal =
            !!status &&
            ['expired', 'past_due', 'suspended', 'inactive'].includes(status);

          const planName = user?.planType || user?.subscriptionPlan || 'Premium';
          const title = isActive
            ? `${planName} plan`
            : needsRenewal
              ? 'Subscription expired'
              : 'Go Premium';

          const description = isActive
            ? null
            : needsRenewal
              ? 'Renew to keep your premium features.'
              : 'Unlimited uploads, insights & more.';

          const ctaLabel = isActive
            ? 'Manage billing'
            : needsRenewal
              ? 'Renew now'
              : 'Upgrade';

          const Icon = needsRenewal ? AlertCircle : Crown;

          return (
            <div className="px-2 pb-3">
              <div className="rounded-2xl border border-[#e9b306]/25 bg-gradient-to-br from-[#fffaec] to-[#fdf1cd] p-3.5">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#e9b306] text-gray-900 shadow-sm">
                    <Icon size={18} strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="truncate text-sm font-semibold capitalize text-gray-900">
                        {title}
                      </h3>
                      {isActive && (
                        <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          {status === 'cancelled' ? 'Cancelled' : 'Active'}
                        </span>
                      )}
                    </div>
                    {description && (
                      <p className="mt-0.5 text-xs leading-snug text-gray-600">
                        {description}
                      </p>
                    )}
                  </div>
                </div>

                <Link
                  to="/account/billing"
                  onClick={onMobileClose}
                  className="mt-3 flex w-full items-center justify-center rounded-lg bg-gray-900 py-2 text-xs font-semibold text-white transition-colors hover:bg-gray-800"
                >
                  {ctaLabel}
                </Link>
              </div>
            </div>
          );
        })()}

        {/* Currency switcher — mobile only. The desktop navbar already has its
            own currency dropdown, so this is hidden on lg+ to avoid duplication. */}
        {isExpanded && (
          <div className="lg:hidden px-2 pb-3">
            <div className="flex items-center justify-between rounded-xl border border-gray-200 px-3 py-2.5">
              <span className="text-sm font-medium text-gray-600">Currency</span>
              <MobileCurrencyMenu />
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 pt-4 space-y-1">
          {bottomLinks.map((link) => (
            <SidebarLink
              key={link.to}
              to={link.to}
              icon={link.icon}
              label={link.label}
              isActive={location.pathname === link.to}
              isExpanded={isExpanded}
              onClick={() => {
                if (link.onClick) {
                  link.onClick();
                } else if (onMobileClose) {
                  onMobileClose();
                }
              }}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Sidebar; 